import { forEach } from 'lodash';
import { observable, action, computed } from 'mobx';
import { setter } from 'mobx-decorators';
import agent from '../services/web/agent';
import commonValidators from '../services/validations/commonValidators';
import configurationsStore from './configurationsStore';
import translationsStore from './translationsStore';
import oidcStore from './oidcStore';

class PasswordPoliciesStore {

    @observable complexity = {
        minPasswordLength : 0,
        minNoOfDigits : 0,
        minNoOfUpperChars : 0,
        minNoOfLowerChars : 0,
        minNoOfSpecialChars : 0,
        passwordAge : 0,
        passwordHistoryEnabled : false,
        passwordsHistoryCount: 0,
        displayForgotPassword: true,
    };

    @observable lockoutOptions = {
        maxFailedAccessAttempts: 0,
        defaultLockoutTimeSpan : 0,
    }

    @observable savingPolicy = false;
    @observable complexityFormErrors = [];
    @observable lockoutFormErrors = [];

    @setter @observable minPwdLenValidationMessage;
    @setter @observable minDigitsValidationMessage;
    @setter @observable minUpperCharsValidationMessage;
    @setter @observable minLowerCharsValidationMessage;
    @setter @observable minSpecialCharsValidationMessage;
    @setter @observable minPwdAgeValidationMessage;
    @setter @observable minPwdHistoryValidationMessage;

    @setter @observable isMinPwdLenValid = false;
    @setter @observable isMinUpperCharValid = false;
    @setter @observable isMinLowerCharValid = false;
    @setter @observable isMinDigitsValid = false;
    @setter @observable isMinSpecialCharValid = false;
    @setter @observable isPwdAgeValid = false;
    @setter @observable isPwdHistoryCntValid = false;

    @setter @observable maxFailedAttemptsValidationMessage;
    @setter @observable lockoutTimeValidationMessage;


    @setter @observable isLockoutTimeValid = false;
    @setter @observable isFaileAccessAttemptsValid = false;

   

    @observable PasswordRestrictions = [];
    
    PopulatePolicies(policies) {
        if (policies != null ) {
            this.PasswordRestrictions = [];

            this.setPasswordLength(policies.minPasswordLength);
            this.PasswordRestrictions.push(translationsStore.translateWithArguments('PASSWORD_RESTRICTION_CHARACTER_LIMIT', [this.complexity.minPasswordLength]));
            
            this.setPasswordMinNoOfDigits(policies.minNoOfDigits);
            this.PasswordRestrictions.push(translationsStore.translateWithArguments('PASSWORD_RESTRICTION_CONTAIN_NUMBER', [this.complexity.minNoOfDigits]));

            this.setPasswordMinNoOfUppers(policies.minNoOfUppercaseChars);
            this.PasswordRestrictions.push(translationsStore.translateWithArguments('PASSWORD_RESTRICTION_UPPERCASE', [this.complexity.minNoOfUpperChars]));

            this.setPasswordMinNoOfSpecial(policies.minNoOfSpecialChars);
            this.PasswordRestrictions.push(translationsStore.translateWithArguments('PASSWORD_RESTRICTION_CONTAIN_SYMBOL', [this.complexity.minNoOfSpecialChars]));

            this.setPasswordMinNoOfLowers(policies.minNoOfLowercaseChars);
            this.PasswordRestrictions.push(translationsStore.translateWithArguments('PASSWORD_RESTRICTION_LOWERCASE', [this.complexity.minNoOfLowerChars]));

            this.setPasswordAge(policies.passwordExpiryDays);
            this.setPasswordHistoryCount(policies.passwordHistoryCount);
            if (policies.passwordHistoryCount > 0) {
                this.setPasswordHistoryEnabled(true);
            }
            this.setForgotPasswordLinkSetting(policies.showForgotPasswordLink);
        }
    }

    validateForm() {
        this.complexityFormErrors = [];
        this.validateMinPasswordLength();
        this.validateMinNoOfDigits();
        this.validateMinNoOfLowers();
        this.validateMinNoOfUppers();
        this.validateMinNoOfSpecial()
        this.validatePasswordAge();
        this.validatePasswordHistoryCount();

        if (this.complexityFormErrors.filter((valid) => valid > 0).length > 0) {
            return false;
        }
        return true;
    }

    @action savePasswordPolicies() {
        return agent.PasswordPolicies.setPasswordPolicies(this.complexity.minPasswordLength, this.complexity.minNoOfDigits, this.complexity.minNoOfLowerChars, this.complexity.minNoOfUpperChars, this.complexity.minNoOfSpecialChars, this.complexity.passwordAge, this.complexity.passwordsHistoryCount, this.complexity.displayForgotPassword);
    }

    setPasswordHistoryEnabled(isHistoryEnabled) {
       this.complexity.passwordHistoryEnabled = isHistoryEnabled;
        if (!isHistoryEnabled)
            this.setPasswordHistoryCount(0);
    }

    setPasswordHistoryCount(historyCount) {
        this.complexity.passwordsHistoryCount = historyCount;
    }

    validatePasswordHistoryCount() {
        if (!this.complexity.isHistoryEnabled)
            return true;
        const validation = commonValidators.validateNumericField(this.complexity.passwordsHistoryCount);
        this.minPwdHistoryValidationMessage = validation.errors.first('field');
        this.complexityFormErrors.push(validation.errorCount);
        this.isPwdHistoryCntValid = validation.errorCount == 0 ? true : false;
    }

    setForgotPasswordLinkSetting(val) {
        this.complexity.displayForgotPassword = val;
    }

    setPasswordAge(age) {
        this.complexity.passwordAge = age;
    }

    validatePasswordAge() {
        const validation = commonValidators.validateNumericField(this.complexity.passwordAge);
        this.minPwdAgeValidationMessage = validation.errors.first('field');
        this.complexityFormErrors.push(validation.errorCount);
        this.isPwdAgeValid = validation.errorCount == 0 ? true : false;
    }

    setPasswordLength(length) {
        this.complexity.minPasswordLength = length;
    }

    validateMinPasswordLength() {
        const validation = commonValidators.validateNumericField(this.complexity.minPasswordLength);
        this.minPwdLenValidationMessage = validation.errors.first('field');
        this.complexityFormErrors.push(validation.errorCount);
        this.isMinPwdLenValid = validation.errorCount == 0 ? true : false;
    }

    setPasswordMinNoOfDigits(noofdigits) {
        this.complexity.minNoOfDigits = noofdigits;
    }

    validateMinNoOfDigits() {
        const validation = commonValidators.validateNumericField(this.complexity.minNoOfDigits);
        this.minDigitsValidationMessage = validation.errors.first('field');
        this.complexityFormErrors.push(validation.errorCount);
        this.isMinDigitsValid = validation.errorCount == 0 ? true : false;
    }

    setPasswordMinNoOfLowers(noOfLowerchars) {
        this.complexity.minNoOfLowerChars = noOfLowerchars;
    }

    validateMinNoOfLowers() {
        const validation = commonValidators.validateNumericField(this.complexity.minNoOfLowerChars);
        this.minLowerCharsValidationMessage = validation.errors.first('field');
        this.complexityFormErrors.push(validation.errorCount);
        this.isMinLowerCharValid = validation.errorCount == 0 ? true : false;
    }

    setPasswordMinNoOfUppers(noOfUpperChars) {
        this.complexity.minNoOfUpperChars = noOfUpperChars;
    }

    validateMinNoOfUppers() {
        const validation = commonValidators.validateNumericField(this.complexity.minNoOfUpperChars);
        this.minUpperCharsValidationMessage = validation.errors.first('field');
        this.complexityFormErrors.push(validation.errorCount);
        this.isMinUpperCharValid = validation.errorCount == 0 ? true : false;
    }

    setPasswordMinNoOfSpecial(noOfSpecialChars) {
        this.complexity.minNoOfSpecialChars = noOfSpecialChars;
    }

    validateMinNoOfSpecial() {
        const validation = commonValidators.validateNumericField(this.complexity.minNoOfSpecialChars);
        this.minSpecialCharsValidationMessage = validation.errors.first('field');
        this.complexityFormErrors.push(validation.errorCount);
        this.isMinSpecialCharValid = validation.errorCount == 0 ? true : false;
    }
  
    getPasswordPoliciesForTenant(tenant) {
        agent.PasswordPolicies.getPasswordPoliciesForTenant(tenant)
            .then(action((policies) => {
                this.PopulatePolicies(policies);
            }));
    }

    getPasswordPoliciesForDefaultTenant() {
       agent.PasswordPolicies.getPasswordPolicies()
            .then(action((policies) => {
                this.PopulatePolicies(policies);
            }));
    }

    getPasswordPoliciesForCurrentUser() {
        if (oidcStore.currentUser != null && oidcStore.currentUser.profile.tenant_name != null) {
            this.getPasswordPoliciesForTenant(oidcStore.currentUser.profile.tenant_name);
        }
        else {
            this.getPasswordPoliciesForDefaultTenant();
        }
    }

    @action updatePasswordPolicies(tenant) {
        if (tenant != null && tenant != "") {
            this.getPasswordPoliciesForTenant(tenant);
        }
        else {
            this.getPasswordPoliciesForDefaultTenant();

        }
    }

    getLockoutOptionsForTenant(tenant) {
        agent.PasswordPolicies.getLockoutOptionsForTenant(tenant)
            .then(action((options) => {
                this.PopulateLockoutOptions(options);
            }));
    }

    getLockoutOptionsForDefaultTenant() {
        agent.PasswordPolicies.getLockoutOptions()
            .then(action((options) => {
                this.PopulateLockoutOptions(options);
            }));
    }

    getLockoutOptions() {
        if (oidcStore.currentUser != null && oidcStore.currentUser.profile.tenant_name != null) {
            this.getLockoutOptionsForTenant(oidcStore.currentUser.profile.tenant_name);
        }
        else {
            this.getLockoutOptionsForDefaultTenant();
        }
    }

    PopulateLockoutOptions(options) {
        if (options != null) {
            this.setMaxFailedAttempts(options.maxFailedAccessAttempts);
            this.setLockoutTimespan(options.defaultLockoutTimeSpan);
        }
    }

    setMaxFailedAttempts(attempts) {
        this.lockoutOptions.maxFailedAccessAttempts = attempts;
    }

    setLockoutTimespan(timespan) {
        this.lockoutOptions.defaultLockoutTimeSpan = timespan;
    }

    @action saveLockoutOptions() {
        return agent.PasswordPolicies.setLockoutOptions(this.lockoutOptions.maxFailedAccessAttempts, this.lockoutOptions.defaultLockoutTimeSpan);
    }

    validateLockoutForm() {
        this.lockoutFormErrors = [];
        this.validateMaxFailedAttempts();
        this.validateLockoutTime();

        if (this.lockoutFormErrors.filter((valid) => valid > 0).length > 0) {
            return false;
        }
        return true;
    }

    validateMaxFailedAttempts() {
        const validation = commonValidators.validateNumericField(this.lockoutOptions.maxFailedAccessAttempts);
        this.maxFailedAttemptsValidationMessage = validation.errors.first('field');
        this.lockoutFormErrors.push(validation.errorCount);
        this.isFaileAccessAttemptsValid = validation.errorCount == 0 ? true : false;
    }

    validateLockoutTime() {
        const validation = commonValidators.validateNumericField(this.lockoutOptions.defaultLockoutTimeSpan);
        this.lockoutTimeValidationMessage = validation.errors.first('field');
        this.lockoutFormErrors.push(validation.errorCount);
        this.isLockoutTimeValid = validation.errorCount == 0 ? true : false;
    }
}

export default new PasswordPoliciesStore();


// WEBPACK FOOTER //
// ./src/stores/passwordPoliciesStore.js